/**
 * What the replay HUDs show, as of the bar the playhead is on.
 *
 * <h2>The whole point is "as of", and that is where this can go wrong</h2>
 *
 * A post-game report answers "how did the run end". A replay answers "what did the engine know at
 * 10:35 on the 20th", and every number on screen has to be truncated to that moment or the HUD is
 * quietly showing the viewer the future it exists to hide.
 *
 * <h2>Two kinds of closed trade, and conflating them breaks the display</h2>
 *
 * The replay window is a slice of a longer run — {@code chooseWindow} places it over the densest
 * cluster of decisions, which on real data is nowhere near the start. So by the time the first
 * frame is drawn, trades have ALREADY closed and their profit is ALREADY inside
 * {@code frame.account.equity}.
 *
 * Those arrive stamped {@code frameIndex === -1} and are counted in the OPENING BASELINE: present
 * in the totals from frame zero, never animated. Stamping them 0 instead would make the win count
 * leap on the first bar while the equity beside it had not moved — two numbers describing the same
 * thing and disagreeing, for the whole replay.
 *
 * <h2>Prefix-summed once, read O(1) per frame</h2>
 *
 * The clock can run at 1ms. Recomputing the book on every tick is O(frames x trades) of work on
 * the main thread between paints; the cumulative array is built once when the timeline lands.
 *
 * <h2>Pure</h2>
 *
 * A timeline in, an array of snapshots out. No React, no chart, no network.
 *
 * <h2>IT OWNS NO ARITHMETIC — see utils/portfolioMetrics</h2>
 *
 * It used to. It summed percentages while the Simulator compounded them, so the two reported
 * the same trades and different Net PnL, Equity and Max Drawdown; profit factor agreed by
 * accident, being a ratio of arithmetic sums on both sides, which made the disagreement look
 * smaller than it was. Everything below is now windowing and presentation — which trades are
 * inside the replay, in what order, and what the HUD calls each field. The numbers come from
 * one shared reducer that the Simulator reads too.
 */

import {
  EMPTY_PORTFOLIO, accumulateTrade, finalisePortfolio, profitFactorOf as sharedProfitFactor,
} from './portfolioMetrics';

/**
 * The HUD's own field names, over the shared utility's figures.
 *
 * <p>A thin rename and nothing more. The HUD has always said {@code trades} where
 * LedgerStatistics says {@code tradeCount}, and renaming it at the point of display keeps every
 * call site in the overlay working — while the VALUES all come from one place.
 */
function present(acc, startingEquity) {
  const m = finalisePortfolio(acc, startingEquity);
  return {
    trades: m.tradeCount,
    wins: m.wins,
    losses: m.losses,
    winRatePercent: m.winRatePercent,
    grossProfitPercent: m.grossProfitPercent,
    grossLossPercent: m.grossLossPercent,
    profitFactor: m.profitFactor,
    grossProfitMoney: m.grossProfitMoney,
    grossLossMoney: m.grossLossMoney,
    netMoney: m.netMoney === null ? 0 : m.netMoney,
    peakEquity: m.peakEquity,
    // THE COMPOUNDED FIGURES, which the HUD had no way to show before.
    totalNetPnlPercent: m.totalNetPnlPercent,
    endingEquity: m.endingEquity,
    sized: m.sized,
    // DRAWDOWN: the currency curve when the run is sized, the compounded percentage curve when
    // it is not. An unsized replay used to report a flat 0.00% here — not a measurement, just
    // the absence of a dollar curve to measure, and it read as "riskless".
    maxDrawdownPercent: m.sized ? m.maxDrawdownMoneyPercent : m.maxDrawdownPercent,
  };
}

/** A book with nothing in it. Also what an absent timeline yields. */
export const EMPTY_STATS = Object.freeze(present(EMPTY_PORTFOLIO, null));

/** Re-exported so the HUD keeps one import; the implementation is the shared one. */
export const profitFactorOf = sharedProfitFactor;

export function buildCumulativeStats(timeline) {
  const frames = timeline?.frames || [];
  const stamped = timeline?.trades || [];

  // Pre-window trades form the baseline: already closed when the replay opens, and already
  // inside the first frame's equity.
  //
  // `beforeWindow` is load-bearing, not decoration. A trade can fall outside the window in
  // EITHER direction and the two mean opposite things — one belongs in the baseline, the other
  // has not happened yet at any bar the viewer will see and must be counted nowhere. Treating
  // both as "outside" opened a real SPY replay reporting 30 trades and a profit factor of 22.26
  // on bar 1 of 2,000, which is the future arriving through the HUD.
  // The account's opening balance, read off the first frame that carries one. Absent on an
  // unsized run, which leaves every money field at zero and the drawdown unreported.
  const startingEquity = frames.find(f => f && f.account)?.account?.startingEquity ?? null;

  // The RAW accumulator is folded; each frame's snapshot is presented from it. Folding the
  // presented shape instead would mean re-deriving averages from averages, which is how a
  // rounding error becomes a drift over 2,000 frames.
  let book = stamped
    .filter(s => s && s.frameIndex < 0 && s.beforeWindow)
    .reduce((acc, s) => accumulateTrade(acc, s.trade, startingEquity), EMPTY_PORTFOLIO);

  const byFrame = new Map();
  stamped.filter(s => s && s.frameIndex >= 0).forEach(s => {
    if (!byFrame.has(s.frameIndex)) byFrame.set(s.frameIndex, []);
    byFrame.get(s.frameIndex).push(s.trade);
  });

  const out = [present(book, startingEquity)];
  for (let i = 0; i < frames.length; i++) {
    (byFrame.get(i) || []).forEach(t => { book = accumulateTrade(book, t, startingEquity); });
    out.push(present(book, startingEquity));
  }
  return out;
}

/**
 * Every trade CLOSED AT OR BEFORE this frame, oldest first.
 *
 * <h2>The same bound the cumulative statistics use, and for the same reason</h2>
 *
 * The tiles above the annual table are a fold over exactly this set, so a table built from the
 * whole run would disagree with the panel it sits in -- and worse, it would show years the
 * playhead has not reached. A per-year table of a future the reader is being asked to form a
 * view about is the leak the entire replay is built to prevent.
 *
 * <p>`beforeWindow` trades are included for the same reason the fold includes them: they closed
 * before the session's window opened, so they are part of the book the reader starts with. A
 * frameIndex of -1 means either "before the window" or "after it", and only the first belongs.
 */
export function tradesClosedBy(timeline, index) {
  return (timeline?.trades || [])
    .filter(s => s && ((s.frameIndex >= 0 && s.frameIndex <= index)
      || (s.frameIndex < 0 && s.beforeWindow)))
    .map(s => s.trade);
}

/** The trades that closed exactly on this frame — what the top HUD reports as "just closed". */
export function tradesClosingAt(timeline, index) {
  return (timeline?.trades || [])
    .filter(s => s && s.frameIndex === index)
    .map(s => s.trade);
}

/** The transitions stamped on this frame, newest last. */
export function transitionsAt(timeline, index) {
  return (timeline?.transitions || [])
    .filter(s => s && s.frameIndex === index)
    .map(s => s.event);
}

/**
 * Everything the HUDs need for one playhead position.
 *
 * @param cumulative the array from {@link buildCumulativeStats}, passed in rather than rebuilt so
 *                   a 1ms clock does not redo O(frames x trades) between paints
 */
export function replayStateAt(timeline, index, cumulative) {
  const frames = timeline?.frames || [];
  if (frames.length === 0) {
    return {
      frame: null, stats: EMPTY_STATS, account: null, activeTrade: null,
      justClosed: [], transitions: [], index: 0, total: 0,
    };
  }
  const i = Math.max(0, Math.min(index, frames.length - 1));
  const frame = frames[i];
  const stats = (cumulative && cumulative[i + 1]) || EMPTY_STATS;

  return {
    frame,
    stats,
    account: frame.account || null,
    // The engine's own view of the open premise at this bar — entry, stop, trailing floor, open R.
    // Never re-derived here: a second implementation of "where is the stop now" is a second
    // engine, and the one on the wire is the one that made the decisions.
    activeTrade: frame.trade || null,
    justClosed: tradesClosingAt(timeline, i),
    transitions: transitionsAt(timeline, i),
    index: i,
    total: frames.length,
  };
}

/* ── formatting, kept here so the HUD components stay about layout ────────────────────────── */

export function fmtMoney(value) {
  return Number.isFinite(value)
    ? value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';
}

export function fmtPercent(value, places = 2) {
  return Number.isFinite(value) ? `${value >= 0 ? '+' : ''}${value.toFixed(places)}%` : '—';
}

export function fmtPrice(value, places = 2) {
  return Number.isFinite(value) ? value.toFixed(places) : '—';
}

/** Profit factor for display. "n/a" when there is no denominator — never a big number. */
export function fmtProfitFactor(value) {
  return Number.isFinite(value) ? value.toFixed(2) : 'n/a';
}

/** The session date an engine timestamp belongs to. See dynaManTrades.sessionDateOf. */
export function sessionDateOf(isoTimestamp) {
  if (typeof isoTimestamp !== 'string' || isoTimestamp.length < 10) return null;
  const date = isoTimestamp.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null;
}

/** The clock time, for a frame whose timeframe is intraday. */
export function sessionTimeOf(isoTimestamp) {
  if (typeof isoTimestamp !== 'string') return null;
  const m = /^\d{4}-\d{2}-\d{2}T(\d{2}:\d{2})/.exec(isoTimestamp);
  return m ? m[1] : null;
}
